import React, { useState, useEffect, useContext, useCallback } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { UserContext } from '../App';
import { useTranslation } from '../i18n';
import { billingAPI, subscriptionAPI, codesAPI } from '../api';
import Header from '../components/Header';
import { CreditCard, Check, Zap, TrendingUp, Shield, Loader2, Gift } from 'lucide-react';
import { toast } from 'sonner';

// Redeem Code Section Component
const RedeemCodeSection = ({ lang, onRedeemSuccess }) => {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleRedeem = async () => {
    if (!code.trim()) {
      setError(lang === 'en' ? 'Please enter a code' : 'Por favor ingresa un código');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await codesAPI.redeem(code.trim());
      const { analyses_granted, features_unlocked } = response.data;
      
      toast.success(
        lang === 'en' 
          ? `Code redeemed! You now have ${analyses_granted} analyses available.`
          : `¡Código canjeado! Ahora tienes ${analyses_granted} análisis disponibles.`
      );
      
      setCode('');
      if (onRedeemSuccess) onRedeemSuccess();
    } catch (err) {
      const errorMsg = err.response?.data?.detail || 
        (lang === 'en' ? 'Failed to redeem code' : 'Error al canjear el código');
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 sm:p-8 border border-blue-100 mb-8">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-blue-100 rounded-lg">
          <Gift className="w-5 h-5 text-blue-600" />
        </div>
        <h3 className="text-lg font-semibold text-slate-800">
          {lang === 'en' ? 'Redeem Access Code' : 'Canjear Código de Acceso'}
        </h3>
      </div>
      <p className="text-sm text-slate-600 mb-4">
        {lang === 'en' 
          ? 'Have an access code? Enter it below to unlock your analyses.'
          : '¿Tienes un código de acceso? Ingrésalo abajo para desbloquear tus análisis.'}
      </p>
      <div className="flex flex-col sm:flex-row gap-3">
        <input
          type="text"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase());
            setError(null);
          }}
          placeholder={lang === 'en' ? 'CHEK-XXXX-XXXX' : 'CHEK-XXXX-XXXX'}
          className="flex-1 px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-center uppercase tracking-wider"
          data-testid="redeem-code-input"
        />
        <button
          onClick={handleRedeem}
          disabled={loading || !code.trim()}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
          data-testid="redeem-code-btn"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Gift className="w-4 h-4" />
          )}
          {lang === 'en' ? 'Redeem' : 'Canjear'}
        </button>
      </div>
      {error && (
        <p className="mt-3 text-sm text-red-600" data-testid="redeem-error">
          {error}
        </p>
      )}
    </div>
  );
};

const Billing = () => {
  const { user } = useContext(UserContext);
  const navigate = useNavigate();
  const { lang } = useTranslation();
  const [searchParams] = useSearchParams();
  
  const [subscription, setSubscription] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkoutLoading, setCheckoutLoading] = useState(null);
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [canceling, setCanceling] = useState(false);
  const [cancelInfo, setCancelInfo] = useState(null);  // {end_date_str, days_remaining} after cancel

  // Handle success/cancel query params
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      toast.success(lang === 'en' ? 'Subscription activated!' : '¡Suscripción activada!');
    } else if (searchParams.get('canceled') === 'true') {
      toast.info(lang === 'en' ? 'Checkout cancelled' : 'Pago cancelado');
    }
  }, [searchParams, lang]);

  const loadBillingData = useCallback(async () => {
    try {
      // Load subscription info
      try {
        const subResponse = await billingAPI.getSubscription();
        setSubscription(subResponse.data);
      } catch (e) {
        console.log('No subscription found');
      }
      
      // Load usage data
      const userId = user?.user_id || 'demo_user';
      const usageResponse = await subscriptionAPI.getUsageInfo(userId);
      setUsage(usageResponse.data);
    } catch (error) {
      console.error('Failed to load billing data:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadBillingData();
  }, [loadBillingData]);

  const handleUpgrade = async (planId) => {
    if (!user) {
      toast.error(lang === 'en' ? 'Please sign in to upgrade' : 'Inicia sesión para mejorar');
      return;
    }
    
    setCheckoutLoading(planId);
    try {
      const response = await billingAPI.createCheckout({
        plan_id: planId,
        user_id: user.user_id,
        origin_url: window.location.origin
      });
      
      // Backend returns the Stripe URL in `url` (billing_service). Read that first;
      // fall back to other names for safety. Surface a loud error if absent so a
      // silent dead click (the original bug) can never hide again.
      const checkoutUrl = response.data.url || response.data.checkout_url || response.data.session_url;
      if (checkoutUrl) {
        window.location.href = checkoutUrl;
      } else {
        console.error('Checkout: no URL in response', response.data);
        toast.error(lang === 'en' ? 'Could not open checkout' : 'No se pudo abrir el pago');
        setCheckoutLoading(null);
      }
    } catch (error) {
      console.error('Checkout failed:', error);
      toast.error(lang === 'en' ? 'Failed to start checkout' : 'Error al iniciar pago');
      setCheckoutLoading(null);
    }
  };

  // 3 plans: Free, Investor ($49), Pro ($99)
  // Internal IDs: free→FREE, pro→PRO (displays "Investor"), pro_plus→PRO_PLUS (displays "Pro")
  const plans = [
    {
      id: 'free',
      name: lang === 'en' ? 'Free' : 'Gratis',
      price: 0,
      period: '',
      features: [
        lang === 'en' ? '3 lifetime analyses' : '3 análisis de por vida',
        lang === 'en' ? 'Full property analysis' : 'Análisis completo',
        lang === 'en' ? 'PDF export' : 'Exportar PDF',
      ],
      cta: lang === 'en' ? 'Current Plan' : 'Plan Actual',
      highlighted: false,
    },
    {
      id: 'pro',
      name: 'Investor',  // Display name
      price: 49,
      period: lang === 'en' ? '/month' : '/mes',
      features: [
        lang === 'en' ? '50 analyses per month' : '50 análisis por mes',
        lang === 'en' ? 'Save & compare deals' : 'Guardar y comparar tratos',
        lang === 'en' ? 'Add custom comparables' : 'Comparables manuales',
        lang === 'en' ? 'Deal inbox' : 'Buzón de tratos',
        lang === 'en' ? 'Email support' : 'Soporte por email',
      ],
      cta: lang === 'en' ? 'Upgrade to Investor' : 'Mejorar a Investor',
      highlighted: true,
    },
    {
      id: 'pro_plus',
      name: 'Pro',  // Display name
      price: 99,
      period: lang === 'en' ? '/month' : '/mes',
      features: [
        lang === 'en' ? '200 analyses per month' : '200 análisis por mes',
        lang === 'en' ? 'Everything in Investor' : 'Todo en Investor',
        lang === 'en' ? 'Bulk comparison' : 'Comparación masiva',
        lang === 'en' ? 'Priority support' : 'Soporte prioritario',
      ],
      cta: lang === 'en' ? 'Upgrade to Pro' : 'Mejorar a Pro',
      highlighted: false,
    },
  ];

  const currentPlan = subscription?.plan_id || user?.plan_id || 'free';
  const isPaidPlan = currentPlan === 'pro' || currentPlan === 'pro_plus';
  const isFounderAcct = subscription?.plan_type === 'FOUNDER' || usage?.period === 'unlimited';
  const isCanceling = !!subscription?.cancel_at_period_end || !!cancelInfo;
  const periodEndStr = cancelInfo?.end_date_str
    || (subscription?.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : null);

  const handleCancelSubscription = async () => {
    setCanceling(true);
    try {
      const res = await billingAPI.cancel();
      setCancelInfo({ end_date_str: res.data.end_date_str, days_remaining: res.data.days_remaining });
      setShowCancelDialog(false);
      toast.success(lang === 'en'
        ? `Subscription canceled. You keep access until ${res.data.end_date_str}.`
        : `Suscripción cancelada. Mantienes acceso hasta ${res.data.end_date_str}.`);
      loadBillingData();
    } catch (err) {
      toast.error(err?.response?.data?.detail
        || (lang === 'en' ? 'Could not cancel subscription. Please try again.' : 'No se pudo cancelar. Inténtalo de nuevo.'));
    } finally {
      setCanceling(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <div className="container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-6xl">
        {/* Page Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold text-primary mb-2 sm:mb-4">
            {lang === 'en' ? 'Plans & Billing' : 'Planes y Facturación'}
          </h1>
          <p className="text-base sm:text-lg text-muted-foreground px-4">
            {lang === 'en' 
              ? 'Choose the plan that fits your investment needs'
              : 'Elige el plan que se ajuste a tus necesidades de inversión'}
          </p>
        </div>

        {/* Usage Summary */}
        {usage && (
          <div className="bg-white rounded-xl shadow-sm border border-border p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h2 className="text-base sm:text-lg font-semibold mb-1">
                  {usage?.period === 'monthly' 
                    ? (lang === 'en' ? 'Monthly Usage' : 'Uso Mensual')
                    : usage?.period === 'code'
                    ? (lang === 'en' ? 'Code Usage' : 'Uso del Código')
                    : usage?.period === 'unlimited'
                    ? (lang === 'en' ? 'Founder Access' : 'Acceso Founder')
                    : (lang === 'en' ? 'Lifetime Usage' : 'Uso de por Vida')}
                </h2>
                <p className="text-muted-foreground text-xs sm:text-sm">
                  {/* Conditional reset text based on period */}
                  {usage?.period === 'monthly' && usage?.next_reset_date ? (
                    lang === 'en' 
                      ? `Resets on ${new Date(usage.next_reset_date).toLocaleDateString()}`
                      : `Se reinicia el ${new Date(usage.next_reset_date).toLocaleDateString()}`
                  ) : usage?.period === 'code' ? (
                    lang === 'en' ? 'No monthly reset - use at your own pace' : 'Sin reinicio mensual'
                  ) : usage?.period === 'unlimited' ? (
                    lang === 'en' ? 'Unlimited access' : 'Acceso ilimitado'
                  ) : (
                    lang === 'en' ? 'Lifetime free analyses' : 'Análisis gratis de por vida'
                  )}
                </p>
              </div>
              <div className="text-left sm:text-right">
                <div className="text-2xl sm:text-3xl font-bold text-primary">
                  {usage?.period === 'unlimited' ? (
                    <span className="text-purple-600">∞</span>
                  ) : (
                    <>{usage?.analyses_used ?? 0} / {usage?.analyses_limit ?? 5}</>
                  )}
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground">
                  {lang === 'en' ? 'Analyses used' : 'Análisis usados'}
                </div>
              </div>
            </div>
            {(currentPlan === 'free' || usage?.period === 'lifetime') && (
              <div className="mt-4 pt-4 border-t border-border">
                <div className="w-full bg-slate-200 rounded-full h-2">
                  <div 
                    className="bg-secondary rounded-full h-2 transition-all"
                    style={{ width: `${Math.min(((usage?.analyses_used ?? 0) / (usage?.analyses_limit ?? 5)) * 100, 100)}%` }}
                  ></div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Manage subscription (paid, non-founder) — self-cancel at period end */}
        {isPaidPlan && !isFounderAcct && (
          <div className="bg-white rounded-xl shadow-sm border border-border p-4 sm:p-6 mb-6 sm:mb-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <h3 className="font-semibold text-foreground">
                  {lang === 'en' ? 'Manage subscription' : 'Administrar suscripción'}
                </h3>
                {isCanceling ? (
                  <p className="text-sm text-amber-700 mt-1" data-testid="canceling-status">
                    {lang === 'en'
                      ? `Canceling — you keep access until ${periodEndStr || 'the end of your billing period'}.`
                      : `Cancelando — mantienes acceso hasta ${periodEndStr || 'el fin de tu período'}.`}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground mt-1">
                    {lang === 'en'
                      ? 'Cancel anytime — you keep full access until your paid period ends.'
                      : 'Cancela cuando quieras — mantienes acceso hasta el fin del período pagado.'}
                  </p>
                )}
              </div>
              {!isCanceling && (
                <button
                  onClick={() => setShowCancelDialog(true)}
                  className="px-4 py-2 text-sm font-medium text-red-600 border border-red-200 rounded-lg hover:bg-red-50 transition-colors whitespace-nowrap"
                  data-testid="cancel-subscription-btn"
                >
                  {lang === 'en' ? 'Cancel subscription' : 'Cancelar suscripción'}
                </button>
              )}
            </div>
          </div>
        )}

        {/* Cancel confirmation dialog */}
        {showCancelDialog && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
               onClick={() => !canceling && setShowCancelDialog(false)}>
            <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
              <h3 className="text-lg font-bold text-foreground mb-2">
                {lang === 'en' ? 'Cancel your subscription?' : '¿Cancelar tu suscripción?'}
              </h3>
              <p className="text-sm text-muted-foreground mb-3">
                {lang === 'en'
                  ? `Are you sure? You won't be charged next month, there's no refund, and you keep full access until ${periodEndStr || 'your current period ends'}. After that you move to the Free plan: paid features (saved deals, comparisons, inbox) become unavailable until you subscribe again.`
                  : `¿Seguro? No se te cobrará el próximo mes, no hay reembolso, y mantienes acceso completo hasta ${periodEndStr || 'el fin de tu período actual'}. Después pasas al plan Free: las funciones de pago (tratos guardados, comparaciones, inbox) dejan de estar disponibles hasta que vuelvas a suscribirte.`}
              </p>
              {/* Cancelling a subscription and deleting an account are two different
                  requests (Privacy Policy §17). Said here, where the confusion happens. */}
              <p className="text-xs text-slate-600 mb-3 rounded-lg bg-slate-50 border border-slate-200 p-2.5" data-testid="cancel-is-not-delete">
                {lang === 'en'
                  ? 'Cancelling does NOT delete your account or your data — your account stays and your analyses remain stored. To delete your account, use '
                  : 'Cancelar NO elimina tu cuenta ni tus datos — tu cuenta sigue y tus análisis quedan guardados. Para eliminar tu cuenta, usa '}
                <Link to="/settings#privacy" className="text-indigo-600 hover:text-indigo-700 underline underline-offset-2">
                  {lang === 'en' ? 'Settings → Privacy & Data' : 'Configuración → Privacidad y Datos'}
                </Link>
                .
              </p>
              <p className="text-xs text-slate-500 mb-4">
                {lang === 'en'
                  ? 'Per our Refund Policy ('
                  : 'Según nuestra Política de Reembolsos ('}
                <Link to="/terms" className="text-indigo-600 hover:text-indigo-700 underline underline-offset-2">
                  {lang === 'en' ? 'Terms of Service, Section 10' : 'Términos de Servicio, Sección 10'}
                </Link>
                {lang === 'en'
                  ? '), no refund is issued for the unused portion of the current billing period.'
                  : '), no se emite reembolso por la porción no utilizada del período actual.'}
              </p>
              <div className="flex gap-3 justify-end">
                <button onClick={() => setShowCancelDialog(false)} disabled={canceling}
                  className="px-4 py-2 text-sm font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50 disabled:opacity-50">
                  {lang === 'en' ? 'Keep subscription' : 'Mantener'}
                </button>
                <button onClick={handleCancelSubscription} disabled={canceling}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50"
                  data-testid="confirm-cancel-btn">
                  {canceling ? (lang === 'en' ? 'Canceling…' : 'Cancelando…') : (lang === 'en' ? 'Yes, cancel' : 'Sí, cancelar')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Redeem Code Section */}
        <RedeemCodeSection lang={lang} onRedeemSuccess={loadBillingData} />

        {/* Plans Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-secondary mx-auto"></div>
            <p className="mt-4 text-muted-foreground">{lang === 'en' ? 'Loading plans...' : 'Cargando planes...'}</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            {plans.map((plan) => (
              <div
                key={plan.id}
                className={`rounded-2xl p-6 sm:p-8 ${
                  plan.highlighted
                    ? 'bg-secondary text-white shadow-xl lg:scale-105 relative'
                    : 'bg-white border-2 border-border hover:shadow-lg'
                } transition-all`}
                data-testid={`plan-${plan.id}`}
              >
                {plan.highlighted && (
                  <div className="absolute -top-3 sm:-top-4 left-1/2 -translate-x-1/2 px-3 sm:px-4 py-1 bg-warning text-white text-xs sm:text-sm font-semibold rounded-full">
                    {lang === 'en' ? 'POPULAR' : 'POPULAR'}
                  </div>
                )}
                
                <h3 className="text-xl sm:text-2xl font-bold mb-2">{plan.name}</h3>
                <div className="flex items-baseline mb-4">
                  <span className="text-3xl sm:text-4xl font-bold">${plan.price}</span>
                  <span className={`text-sm sm:text-base ${plan.highlighted ? 'opacity-80' : 'text-muted-foreground'}`}>
                    {plan.period}
                  </span>
                </div>
                
                <ul className="space-y-2 sm:space-y-3 mb-6 sm:mb-8">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check className={`w-4 h-4 sm:w-5 sm:h-5 mt-0.5 flex-shrink-0 ${plan.highlighted ? '' : 'text-success'}`} />
                      <span className="text-sm sm:text-base">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                {currentPlan === plan.id ? (
                  <button
                    disabled
                    className={`w-full px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold text-sm sm:text-base ${
                      plan.highlighted
                        ? 'bg-white/20 text-white cursor-not-allowed'
                        : 'bg-slate-100 text-slate-500 cursor-not-allowed'
                    }`}
                  >
                    {lang === 'en' ? 'Current Plan' : 'Plan Actual'}
                  </button>
                ) : (
                  <button
                    onClick={() => handleUpgrade(plan.id)}
                    disabled={checkoutLoading === plan.id || plan.id === 'free'}
                    className={`w-full px-4 sm:px-6 py-2.5 sm:py-3 rounded-lg font-semibold transition-all flex items-center justify-center gap-2 text-sm sm:text-base ${
                      plan.highlighted
                        ? 'bg-white text-secondary hover:bg-slate-50'
                        : 'bg-secondary text-white hover:bg-blue-700'
                    } disabled:opacity-50`}
                    data-testid={`upgrade-${plan.id}`}
                  >
                    {checkoutLoading === plan.id ? (
                      <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                    ) : (
                      <>
                        <Zap className="w-4 h-4" />
                        {plan.cta}
                      </>
                    )}
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Features Comparison */}
        <div className="mt-12 sm:mt-16">
          <h2 className="text-xl sm:text-2xl font-bold text-center mb-6 sm:mb-8">
            {lang === 'en' ? 'Why Upgrade?' : '¿Por qué mejorar?'}
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            <div className="bg-white rounded-xl p-4 sm:p-6 border border-border">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-secondary" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold mb-2">
                {lang === 'en' ? 'High-Volume Analysis' : 'Análisis de Alto Volumen'}
              </h3>
              <p className="text-muted-foreground text-sm">
                {lang === 'en'
                  ? 'Analyze more deals per month with higher plan limits.'
                  : 'Analiza más tratos por mes con límites de plan más altos.'}
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 sm:p-6 border border-border">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-success" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold mb-2">
                {lang === 'en' ? 'Deal Inbox' : 'Buzón de Tratos'}
              </h3>
              <p className="text-muted-foreground text-sm">
                {lang === 'en'
                  ? 'Receive deals directly from wholesalers and agents.'
                  : 'Recibe tratos directamente de mayoristas y agentes.'}
              </p>
            </div>
            <div className="bg-white rounded-xl p-4 sm:p-6 border border-border sm:col-span-2 lg:col-span-1">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-100 rounded-lg flex items-center justify-center mb-4">
                <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-warning" />
              </div>
              <h3 className="text-base sm:text-lg font-semibold mb-2">
                {lang === 'en' ? 'Priority Support' : 'Soporte Prioritario'}
              </h3>
              <p className="text-muted-foreground text-sm">
                {lang === 'en'
                  ? 'Get help when you need it with dedicated support.'
                  : 'Obtén ayuda cuando la necesites con soporte dedicado.'}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Billing;
